import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, X, Edit2, Trash2, Loader2, CheckCircle, MapPin, Tag, Megaphone, CreditCard, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCommerce() {
  const [tab, setTab] = useState("zones");

  const TABS = [
    { id: "zones", icon: MapPin, label: "Delivery Zones" },
    { id: "promos", icon: Tag, label: "Promo Codes" },
    { id: "banners", icon: Megaphone, label: "Banners" },
    { id: "bank", icon: CreditCard, label: "Bank Transfer" },
  ];

  return (
    <div data-testid="admin-commerce">
      <div className="mb-8">
        <h1 className="font-heading text-3xl text-[#1C1A19]">Commerce Settings</h1>
        <p className="font-body text-sm text-[#8A857D] mt-1">Manage delivery, promos, banners, and payment settings</p>
      </div>

      <div className="flex gap-1 p-1 bg-[#FDF8F6] border border-[#D4859B]/15 rounded-2xl w-fit mb-8">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`commerce-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-body transition-all ${
              tab === t.id ? "bg-seikat-pink text-white shadow-sm" : "text-[#4A453F] hover:text-[#1C1A19] hover:bg-white/60"
            }`}
          >
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
          {tab === "zones" && <DeliveryZonesTab />}
          {tab === "promos" && <PromoCodesTab />}
          {tab === "banners" && <BannersTab />}
          {tab === "bank" && <BankTransferTab />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ===================== DELIVERY ZONES =====================
function DeliveryZonesTab() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name_en: "", name_id: "", fee: "", estimated_time_en: "", estimated_time_id: "" });
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/delivery-zones`, { withCredentials: true });
      setZones(r.data.zones || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const openCreate = () => { setForm({ name_en: "", name_id: "", fee: "", estimated_time_en: "", estimated_time_id: "" }); setModal("create"); };
  const openEdit = (z) => { setForm({ name_en: z.name_en, name_id: z.name_id, fee: z.fee, estimated_time_en: z.estimated_time_en || "", estimated_time_id: z.estimated_time_id || "" }); setModal(z); };

  const save = async () => {
    if (!form.name_en || !form.fee) { toast.error("Name and fee required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, fee: parseInt(form.fee) };
      if (modal === "create") {
        await axios.post(`${API}/admin/delivery-zones`, payload, { withCredentials: true });
        toast.success("Zone created");
      } else {
        await axios.put(`${API}/admin/delivery-zones/${modal.zone_id}`, payload, { withCredentials: true });
        toast.success("Zone updated");
      }
      setModal(null); fetch();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const deactivate = async (z) => {
    try {
      await axios.delete(`${API}/admin/delivery-zones/${z.zone_id}`, { withCredentials: true });
      toast.success(z.status === "active" ? "Zone deactivated" : "Zone update"); fetch();
    } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="font-body text-sm text-[#8A857D]">{zones.filter(z => z.status === "active").length} active zones</p>
        <button onClick={openCreate} className="btn-pill btn-pill--solid gap-2"><Plus size={13} /> Add Zone</button>
      </div>
      {loading ? <Loader2 className="animate-spin text-seikat-pink mx-auto mt-10" size={24} /> : (
        <div className="bg-white rounded-2xl border border-[#D4859B]/12 overflow-hidden shadow-sm">
          <table className="w-full">
            <thead>
              <tr className="bg-[#FDF8F6] border-b border-[#D4859B]/10">
                {["Zone","Name (ID)","Fee","Est. Time","Status",""].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-[10px] tracking-widest uppercase text-[#A8A29A] font-body">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#D4859B]/8">
              {zones.map(z => (
                <tr key={z.zone_id} className="hover:bg-[#FDF8F6]/50" data-testid={`zone-row-${z.zone_id}`}>
                  <td className="px-4 py-3 font-body text-sm font-medium text-[#1C1A19]">{z.name_en}</td>
                  <td className="px-4 py-3 font-body text-sm text-[#4A453F]">{z.name_id}</td>
                  <td className="px-4 py-3 font-body text-sm text-[#1C1A19]">Rp {z.fee?.toLocaleString("id-ID")}</td>
                  <td className="px-4 py-3 font-body text-xs text-[#8A857D]">{z.estimated_time_en}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-body ${
                      z.status === "active" ? "bg-[#C5DB7B]/20 text-[#5A7A1A]" : "bg-[#A8A29A]/15 text-[#6A6560]"
                    }`}>{z.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(z)} className="w-7 h-7 rounded-lg hover:bg-[#D4859B]/12 flex items-center justify-center text-[#A8A29A] hover:text-[#D4859B]"><Edit2 size={13} /></button>
                      <button onClick={() => deactivate(z)} className="w-7 h-7 rounded-lg hover:bg-red-50 flex items-center justify-center text-[#A8A29A] hover:text-red-400"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ZoneModal modal={modal} form={form} setForm={setForm} saving={saving} onSave={save} onClose={() => setModal(null)} />
    </div>
  );
}

function ZoneModal({ modal, form, setForm, saving, onSave, onClose }) {
  if (!modal) return null;
  return (
    <div className="fixed inset-0 z-50 bg-[#1C1A19]/40 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-5">
          <h3 className="font-heading text-lg text-[#1C1A19]">{modal === "create" ? "New Delivery Zone" : "Edit Zone"}</h3>
          <button onClick={onClose} className="w-7 h-7 rounded-full hover:bg-[#D4859B]/10 flex items-center justify-center"><X size={15} /></button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#A8A29A] mb-1">Name (EN) *</label>
              <input value={form.name_en} onChange={e => setForm(p => ({...p, name_en: e.target.value}))} className="admin-input w-full" placeholder="South Jakarta" />
            </div>
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#A8A29A] mb-1">Nama (ID) *</label>
              <input value={form.name_id} onChange={e => setForm(p => ({...p, name_id: e.target.value}))} className="admin-input w-full" placeholder="Jakarta Selatan" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] tracking-widest uppercase text-[#A8A29A] mb-1">Delivery Fee (Rp) *</label>
            <input type="number" value={form.fee} onChange={e => setForm(p => ({...p, fee: e.target.value}))} className="admin-input w-full" placeholder="25000" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#A8A29A] mb-1">Est. Time (EN)</label>
              <input value={form.estimated_time_en} onChange={e => setForm(p => ({...p, estimated_time_en: e.target.value}))} className="admin-input w-full" placeholder="Same day (2-4 hours)" />
            </div>
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#A8A29A] mb-1">Est. Waktu (ID)</label>
              <input value={form.estimated_time_id} onChange={e => setForm(p => ({...p, estimated_time_id: e.target.value}))} className="admin-input w-full" placeholder="Hari yang sama (2-4 jam)" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn-pill">Cancel</button>
          <button onClick={onSave} disabled={saving} className="btn-pill btn-pill--solid">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />}
            {modal === "create" ? "Create" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===================== PROMO CODES =====================
function PromoCodesTab() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ code: "", discount_type: "percentage", discount_value: "", min_order: "", max_discount: "", usage_limit: "", valid_until: "", status: "active" });
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/promo-codes`, { withCredentials: true }); setPromos(r.data.promo_codes || r.data.promos || []); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    if (!form.code || !form.discount_value) { toast.error("Code and discount value required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, discount_value: parseFloat(form.discount_value), min_order: parseFloat(form.min_order || 0), max_discount: parseFloat(form.max_discount || 0), usage_limit: parseInt(form.usage_limit || 0) };
      if (modal === "create") { await axios.post(`${API}/admin/promo-codes`, payload, { withCredentials: true }); toast.success("Promo created"); }
      else { await axios.put(`${API}/admin/promo-codes/${modal.code_id}`, payload, { withCredentials: true }); toast.success("Promo updated"); }
      setModal(null); fetch();
    } catch { toast.error("Failed"); }
    setSaving(false);
  };

  const togglePromo = async (p) => {
    try { await axios.put(`${API}/admin/promo-codes/${p.code_id}`, { status: p.status === "active" ? "inactive" : "active" }, { withCredentials: true }); fetch(); }
    catch { toast.error("Failed"); }
  };

  const openCreate = () => { setForm({ code: "", discount_type: "percentage", discount_value: "", min_order: "", max_discount: "", usage_limit: "", valid_until: "", status: "active" }); setModal("create"); };
  const openEdit = (p) => { setForm({ code: p.code, discount_type: p.discount_type, discount_value: p.discount_value, min_order: p.min_order || "", max_discount: p.max_discount || "", usage_limit: p.usage_limit || "", valid_until: p.valid_until ? p.valid_until.slice(0,10) : "", status: p.status }); setModal(p); };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="font-body text-sm text-[#8A857D]">{promos.length} promo codes</p>
        <button onClick={openCreate} className="btn-pill btn-pill--solid gap-2"><Plus size={13} /> New Promo</button>
      </div>
      {loading ? <Loader2 className="animate-spin text-seikat-pink mx-auto mt-10" size={24} /> : (
        <div className="bg-white rounded-2xl border border-[#D4859B]/12 overflow-hidden shadow-sm">
          <table className="w-full">
            <thead><tr className="bg-[#FDF8F6] border-b border-[#D4859B]/10">
              {["Code","Discount","Min Order","Usage","Expires","Status",""].map(h => <th key={h} className="text-left px-4 py-3 text-[10px] tracking-widest uppercase text-[#A8A29A] font-body">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-[#D4859B]/8">
              {promos.map(p => (
                <tr key={p.code_id} className="hover:bg-[#FDF8F6]/50">
                  <td className="px-4 py-3"><span className="font-mono text-sm font-bold text-[#1C1A19] bg-[#D4859B]/10 px-2.5 py-1 rounded-lg">{p.code}</span></td>
                  <td className="px-4 py-3 font-body text-sm text-[#1C1A19]">
                    {p.discount_type === "percentage" ? `${p.discount_value}%` : `Rp ${p.discount_value?.toLocaleString("id-ID")}`}
                  </td>
                  <td className="px-4 py-3 font-body text-sm text-[#4A453F]">Rp {(p.min_order || 0).toLocaleString("id-ID")}</td>
                  <td className="px-4 py-3 font-body text-sm text-[#4A453F]">{p.used_count}/{p.usage_limit || "∞"}</td>
                  <td className="px-4 py-3 font-body text-xs text-[#8A857D]">{p.valid_until ? p.valid_until.slice(0,10) : "No expiry"}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => togglePromo(p)} className={`text-xs px-2.5 py-1 rounded-full font-body ${
                      p.status === "active" ? "bg-[#C5DB7B]/20 text-[#5A7A1A]" : "bg-[#A8A29A]/15 text-[#6A6560]"
                    }`}>{p.status}</button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openEdit(p)} className="w-7 h-7 rounded-lg hover:bg-[#D4859B]/12 flex items-center justify-center text-[#A8A29A] hover:text-[#D4859B]"><Edit2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modal && (
        <div className="fixed inset-0 z-50 bg-[#1C1A19]/40 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-5">
              <h3 className="font-heading text-lg text-[#1C1A19]">{modal === "create" ? "New Promo Code" : "Edit Promo"}</h3>
              <button onClick={() => setModal(null)}><X size={15} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="label-sm">Code *</label>
                <input value={form.code} onChange={e => setForm(p => ({...p, code: e.target.value.toUpperCase()}))} className="admin-input w-full font-mono" placeholder="WELCOME10" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-sm">Type</label>
                  <select value={form.discount_type} onChange={e => setForm(p => ({...p, discount_type: e.target.value}))} className="admin-input w-full">
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed (Rp)</option>
                  </select>
                </div>
                <div>
                  <label className="label-sm">Value *</label>
                  <input type="number" value={form.discount_value} onChange={e => setForm(p => ({...p, discount_value: e.target.value}))} className="admin-input w-full" placeholder={form.discount_type === "percentage" ? "10" : "50000"} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-sm">Min Order (Rp)</label>
                  <input type="number" value={form.min_order} onChange={e => setForm(p => ({...p, min_order: e.target.value}))} className="admin-input w-full" placeholder="100000" />
                </div>
                <div>
                  <label className="label-sm">Max Discount (Rp)</label>
                  <input type="number" value={form.max_discount} onChange={e => setForm(p => ({...p, max_discount: e.target.value}))} className="admin-input w-full" placeholder="50000" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-sm">Usage Limit</label>
                  <input type="number" value={form.usage_limit} onChange={e => setForm(p => ({...p, usage_limit: e.target.value}))} className="admin-input w-full" placeholder="100" />
                </div>
                <div>
                  <label className="label-sm">Valid Until</label>
                  <input type="date" value={form.valid_until} onChange={e => setForm(p => ({...p, valid_until: e.target.value}))} className="admin-input w-full" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setModal(null)} className="btn-pill">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-pill btn-pill--solid">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===================== BANNERS =====================
function BannersTab() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ title_en: "", title_id: "", subtitle_en: "", subtitle_id: "", image_url: "", cta_text_en: "", cta_text_id: "", cta_link: "", status: "active" });
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/banners`, { withCredentials: true }); setBanners(r.data.banners || []); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    if (!form.title_en) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      if (modal === "create") { await axios.post(`${API}/admin/banners`, form, { withCredentials: true }); toast.success("Banner created"); }
      else { await axios.put(`${API}/admin/banners/${modal.banner_id}`, form, { withCredentials: true }); toast.success("Banner updated"); }
      setModal(null); fetch();
    } catch { toast.error("Failed"); }
    setSaving(false);
  };

  const openCreate = () => { setForm({ title_en: "", title_id: "", subtitle_en: "", subtitle_id: "", image_url: "", cta_text_en: "", cta_text_id: "", cta_link: "", status: "active" }); setModal("create"); };
  const openEdit = (b) => { setForm({ title_en: b.title_en || "", title_id: b.title_id || "", subtitle_en: b.subtitle_en || "", subtitle_id: b.subtitle_id || "", image_url: b.image_url || "", cta_text_en: b.cta_text_en || "", cta_text_id: b.cta_text_id || "", cta_link: b.cta_link || "", status: b.status }); setModal(b); };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="font-body text-sm text-[#8A857D]">{banners.length} banners</p>
        <button onClick={openCreate} className="btn-pill btn-pill--solid gap-2"><Plus size={13} /> New Banner</button>
      </div>
      {loading ? <Loader2 className="animate-spin text-seikat-pink mx-auto mt-10" size={24} /> : banners.length === 0 ? (
        <div className="text-center py-16"><Megaphone size={36} className="mx-auto text-[#D4859B]/30 mb-3" /><p className="font-body text-[#A8A29A]">No banners yet</p></div>
      ) : (
        <div className="grid gap-3">
          {banners.map(b => (
            <div key={b.banner_id} className="bg-white rounded-2xl border border-[#D4859B]/12 p-4 flex items-center gap-4">
              {b.image_url && <img src={b.image_url} alt={b.title_en} className="w-16 h-16 rounded-lg object-cover" />}
              <div className="flex-1 min-w-0">
                <p className="font-body font-medium text-[#1C1A19]">{b.title_en}</p>
                <p className="font-body text-xs text-[#8A857D]">{b.subtitle_en}</p>
                {b.cta_link && <p className="font-body text-xs text-seikat-pink">{b.cta_link}</p>}
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full ${
                b.status === "active" ? "bg-[#C5DB7B]/20 text-[#5A7A1A]" : "bg-[#A8A29A]/15 text-[#6A6560]"
              }`}>{b.status}</span>
              <button onClick={() => openEdit(b)} className="w-7 h-7 rounded-lg hover:bg-[#D4859B]/12 flex items-center justify-center text-[#A8A29A] hover:text-[#D4859B]"><Edit2 size={13} /></button>
            </div>
          ))}
        </div>
      )}
      {modal && (
        <div className="fixed inset-0 z-50 bg-[#1C1A19]/40 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-5">
              <h3 className="font-heading text-lg">{modal === "create" ? "New Banner" : "Edit Banner"}</h3>
              <button onClick={() => setModal(null)}><X size={15} /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label-sm">Title (EN) *</label><input value={form.title_en} onChange={e => setForm(p => ({...p, title_en: e.target.value}))} className="admin-input w-full" /></div>
                <div><label className="label-sm">Judul (ID)</label><input value={form.title_id} onChange={e => setForm(p => ({...p, title_id: e.target.value}))} className="admin-input w-full" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label-sm">Subtitle (EN)</label><input value={form.subtitle_en} onChange={e => setForm(p => ({...p, subtitle_en: e.target.value}))} className="admin-input w-full" /></div>
                <div><label className="label-sm">Subjudul (ID)</label><input value={form.subtitle_id} onChange={e => setForm(p => ({...p, subtitle_id: e.target.value}))} className="admin-input w-full" /></div>
              </div>
              <div><label className="label-sm">Image URL</label><input value={form.image_url} onChange={e => setForm(p => ({...p, image_url: e.target.value}))} className="admin-input w-full" placeholder="https://..." /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label-sm">CTA Text (EN)</label><input value={form.cta_text_en} onChange={e => setForm(p => ({...p, cta_text_en: e.target.value}))} className="admin-input w-full" /></div>
                <div><label className="label-sm">CTA Text (ID)</label><input value={form.cta_text_id} onChange={e => setForm(p => ({...p, cta_text_id: e.target.value}))} className="admin-input w-full" /></div>
              </div>
              <div><label className="label-sm">CTA Link</label><input value={form.cta_link} onChange={e => setForm(p => ({...p, cta_link: e.target.value}))} className="admin-input w-full" placeholder="/catalog" /></div>
              <div><label className="label-sm">Status</label>
                <select value={form.status} onChange={e => setForm(p => ({...p, status: e.target.value}))} className="admin-input w-full">
                  <option value="active">Active</option><option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setModal(null)} className="btn-pill">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-pill btn-pill--solid">{saving ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />} Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===================== BANK TRANSFER =====================
function BankTransferTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/bank-transfer`).then(r => setConfig(r.data)).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/bank-transfer`, config, { withCredentials: true });
      toast.success("Bank transfer settings saved!");
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  if (loading) return <Loader2 className="animate-spin text-seikat-pink mx-auto mt-10" size={24} />;
  if (!config) return null;

  return (
    <div className="max-w-2xl">
      <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-6 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div><label className="label-sm">Bank Name</label><input value={config.bank_name || ""} onChange={e => setConfig(p => ({...p, bank_name: e.target.value}))} className="admin-input w-full" placeholder="BCA" data-testid="bank-name" /></div>
          <div><label className="label-sm">Account Number</label><input value={config.account_number || ""} onChange={e => setConfig(p => ({...p, account_number: e.target.value}))} className="admin-input w-full font-mono" placeholder="1234567890" /></div>
        </div>
        <div>
          <label className="label-sm">Account Name</label>
          <input value={config.account_name || ""} onChange={e => setConfig(p => ({...p, account_name: e.target.value}))} className="admin-input w-full" placeholder="PT Seikat Florist Indonesia" />
        </div>
        <div>
          <label className="label-sm">Default Delivery Fee (Rp)</label>
          <input type="number" value={config.delivery_fee || ""} onChange={e => setConfig(p => ({...p, delivery_fee: parseInt(e.target.value)}))} className="admin-input w-full" placeholder="25000" />
        </div>
        <div>
          <label className="label-sm">WhatsApp Number (with country code)</label>
          <input value={config.whatsapp_number || ""} onChange={e => setConfig(p => ({...p, whatsapp_number: e.target.value}))} className="admin-input w-full" placeholder="6281234567890" />
        </div>
        <div>
          <label className="label-sm">Instructions (English)</label>
          <textarea value={config.instructions_en || ""} onChange={e => setConfig(p => ({...p, instructions_en: e.target.value}))} rows={3} className="admin-input w-full resize-none" />
        </div>
        <div>
          <label className="label-sm">Instruksi (Indonesia)</label>
          <textarea value={config.instructions_id || ""} onChange={e => setConfig(p => ({...p, instructions_id: e.target.value}))} rows={3} className="admin-input w-full resize-none" />
        </div>
        <button onClick={save} disabled={saving} className="btn-pill btn-pill--solid" data-testid="save-bank-settings">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save Settings
        </button>
      </div>
    </div>
  );
}
